import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SectionCard } from './SectionCard'
import { Skeleton } from '@/components/ui/skeleton'
import { useContractsToRenew } from '@/hooks/useDashboard'
import { daysUntil, formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import {
  ContractItemIcon,
  ContractsRenewalHeaderIcon,
  DashboardListArrowIcon,
  DashboardListDotIcon,
} from '@/components/icons/DashboardListIcons'

export function ContractsToRenewPanel() {
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()
  const { data, isLoading, isError } = useContractsToRenew()
  const contracts = (data?.data ?? [])
    .slice()
    .sort((a, b) => (a.end_date ?? '').localeCompare(b.end_date ?? ''))
    .slice(0, 3)

  let body: React.ReactNode
  if (isLoading) {
    body = (
      <div className='flex flex-col gap-3'>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className='h-17.5 w-full rounded-xl' />
        ))}
      </div>
    )
  } else if (isError) {
    body = (
      <p className='py-8 text-center text-sm text-destructive'>
        {t('contractsToRenew.unableToLoad')}
      </p>
    )
  } else if (contracts.length === 0) {
    body = (
      <p className='py-8 text-center text-sm text-muted-foreground'>
        {t('contractsToRenew.empty')}
      </p>
    )
  } else {
    body = (
      <ul className='flex flex-col gap-3'>
        {contracts.map(contract => {
          const remaining =
            contract.days_remaining ?? daysUntil(contract.end_date)

          return (
            <li key={contract.id}>
              <button
                type='button'
                onClick={() => navigate(`/contracts/${contract.id}`)}
                className='flex h-17.5 w-full items-start gap-3 overflow-hidden rounded-xl border border-[#e7e9ef] bg-white p-3 text-left transition-colors hover:bg-[#f4f5f8]/50'
              >
                <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#f4f5f8]'>
                  <ContractItemIcon />
                </span>
                <span className='flex min-w-0 flex-1 flex-col gap-1'>
                  <span className='block truncate font-sans text-sm font-medium leading-[1.4] text-[#171a20]'>
                    {t('contractsToRenew.dueTitle', {
                      name:
                        contract.client_name ??
                        contract.number ??
                        t('contractsToRenew.contractFallback'),
                    })}
                  </span>
                  <span className='flex min-w-0 items-center gap-2 font-sans text-sm font-normal leading-[1.4] text-[#3d434e]'>
                    {contract.number && (
                      <>
                        <span className='truncate text-xs'>
                          {contract.number}
                        </span>
                        <DashboardListDotIcon className='size-1 shrink-0' />
                      </>
                    )}
                    <span className='truncate'>
                      {t('contractsToRenew.ends', {
                        date: formatDate(contract.end_date),
                      })}
                    </span>
                  </span>
                </span>
                {remaining !== null && (
                  <span
                    className={cn(
                      'inline-flex h-6 shrink-0 items-center rounded-lg px-2 font-sans text-xs font-medium leading-4',
                      remaining <= 0
                        ? 'bg-[#fecaca] text-[#7f1d1d]'
                        : remaining <= 45
                          ? 'bg-[#ffe7ba] text-[#ad4e00]'
                          : 'bg-[#f4f5f8] text-[#171a20]'
                    )}
                  >
                    {t('contractsToRenew.dayBadge', { count: remaining })}
                  </span>
                )}
              </button>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <SectionCard
      variant='chart'
      title={t('contractsToRenew.title')}
      className='h-86.5'
      headerClassName='min-h-16'
      contentClassName='h-70.5'
      headerIcon={<ContractsRenewalHeaderIcon />}
      headerIconClassName='bg-[#fff1ea]'
      headerRight={
        <Link
          to='/contracts?filter=expiring'
          className='inline-flex items-center gap-2 rounded-lg p-2 font-sans text-xs font-medium leading-none text-[#ff5a1f] hover:underline'
        >
          {t('contractsToRenew.viewAll')}
          <DashboardListArrowIcon />
        </Link>
      }
    >
      {body}
    </SectionCard>
  )
}

export default ContractsToRenewPanel
